import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Box,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EmailIcon from '@mui/icons-material/Email';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { Controller, useFormContext } from 'react-hook-form';
import { NumberTextFieldNoWheelRHF } from './NumberTextFieldNoWheelRHF';
import {
  formatTicketPrice,
  getTicketSaleStatus,
  TICKET_STATUS_COLOR,
  TICKET_STATUS_LABEL,
  buildHiddenTicketUrl,
} from './paymentFormUtils';
import { useBrandRegistrationCopy } from '../../../brands/registrationCopy';
import { useBrand } from '../../../brands/BrandProvider';
import { calculateTicketTotalsWithFee } from '../../../utils/money';
import InlineCopyLink from './InlineCopyLink';

interface TicketCardEditorProps {
  index: number;
  expanded: boolean;
  onToggle: () => void;
  slug?: string;
  parishFeePercentage: number;
  canDeactivate: boolean;
  onDeactivate: () => void;
  onReactivate: () => void;
  getFieldError: (index: number, field: string) => { message?: string } | undefined;
  isTicketPaid: (index: number) => boolean;
  onTicketTypeChange: (index: number, isPaid: boolean) => void;
  onAddEmail: (index: number, emailInput: string) => void;
  onRemoveEmail: (index: number, email: string) => void;
  onToggleRestricted: (index: number, isRestricted: boolean) => void;
  onImportEmails: (index: number, file: File) => void;
}

const TicketCardEditor: React.FC<TicketCardEditorProps> = ({
  index,
  expanded,
  onToggle,
  slug = '',
  parishFeePercentage,
  canDeactivate,
  onDeactivate,
  onReactivate,
  getFieldError,
  isTicketPaid,
  onTicketTypeChange,
  onAddEmail,
  onRemoveEmail,
  onToggleRestricted,
  onImportEmails,
}) => {
  const { control, watch, setValue } = useFormContext();
  const t = useBrandRegistrationCopy();
  const brand = useBrand();
  const ticket = watch(`tickets.${index}`);
  const isActive = ticket?.isActive !== false;
  const ticketIsPaid = isTicketPaid(index);
  const status = getTicketSaleStatus(ticket || {});
  const minPerOrder = Math.max(1, Number(ticket?.minPerOrder) || 1);
  const maxPerOrder = ticket?.maxPerOrder != null && ticket?.maxPerOrder !== ''
    ? Number(ticket.maxPerOrder)
    : null;

  React.useEffect(() => {
    if (minPerOrder > 1 || (maxPerOrder != null && maxPerOrder > 1)) {
      setValue('allowGroupRegistration', true, { shouldDirty: true });
    }
  }, [minPerOrder, maxPerOrder, setValue]);

  if (!isActive) {
    return (
      <Paper
        variant="outlined"
        sx={{
          mb: 1.5,
          p: 2,
          borderLeft: '4px solid',
          borderLeftColor: 'grey.400',
          bgcolor: 'action.hover',
          opacity: 0.85,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {ticket?.name?.trim() || `Ingresso ${index + 1}`}
              <Chip label="Desativado" size="small" />
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('Não aparece para compra; inscrições existentes são mantidas.')}
            </Typography>
          </Box>
          <Tooltip title="Reativar ingresso">
            <IconButton color="primary" onClick={onReactivate} size="small" aria-label="Reativar ingresso">
              <AddCircleOutlineIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>
    );
  }

  const summaryTitle = ticket?.name?.trim() || `Ingresso ${index + 1}`;

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => {
        if (isExpanded !== expanded) onToggle();
      }}
      disableGutters
      elevation={0}
      sx={{
        mb: 1.5,
        border: '1px solid',
        borderColor: expanded ? 'primary.main' : 'divider',
        borderRadius: '8px !important',
        '&:before': { display: 'none' },
        overflow: 'hidden',
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, minHeight: 56 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1, gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {summaryTitle}
            </Typography>
            <Chip size="small" label={formatTicketPrice(ticket?.price)} variant="outlined" />
            <Chip size="small" label={TICKET_STATUS_LABEL[status]} color={TICKET_STATUS_COLOR[status]} />
            {ticket?.hiddenFromPublicPage && <Chip size="small" label="Oculto" variant="outlined" />}
            {ticket?.isRestricted && <Chip size="small" label="Restrito" color="warning" variant="outlined" />}
          </Box>
          {canDeactivate && (
            <Tooltip title="Desativar ingresso">
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeactivate();
                }}
                aria-label="Desativar ingresso"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 2, pb: 3, pt: 0 }}>
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="body2" gutterBottom sx={{ fontWeight: 500, mb: 1 }}>
            Tipo de ingresso
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              row
              value={ticketIsPaid ? 'paid' : 'free'}
              onChange={(e) => onTicketTypeChange(index, e.target.value === 'paid')}
            >
              <FormControlLabel value="free" control={<Radio />} label="Gratuito" />
              <FormControlLabel value="paid" control={<Radio />} label="Pago" />
            </RadioGroup>
            <FormHelperText>
              {ticketIsPaid ? 'Os participantes pagarão por este ingresso' : 'Este ingresso será gratuito'}
            </FormHelperText>
          </FormControl>
        </Box>

        <Grid container spacing={2.5}>
          <Grid item xs={12} md={6}>
            <Controller
              name={`tickets.${index}.name`}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Nome do ingresso *"
                  placeholder="Ex: Lote 1, VIP, Meia-entrada"
                  error={!!getFieldError(index, 'name')}
                  helperText={getFieldError(index, 'name')?.message}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name={`tickets.${index}.description`}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Descrição"
                  placeholder="Ex: Válido até 15/06"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name={`tickets.${index}.price`}
              control={control}
              render={({ field }) => (
                <NumberTextFieldNoWheelRHF
                  field={field}
                  fullWidth
                  type="number"
                  label={ticketIsPaid ? 'Preço (R$) *' : 'Preço (R$)'}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                    inputProps: { min: 0, step: 0.01 },
                  }}
                  error={!!getFieldError(index, 'price')}
                  helperText={
                    getFieldError(index, 'price')?.message ||
                    (ticketIsPaid ? 'Defina o preço do ingresso' : 'Ingresso gratuito — R$ 0,00')
                  }
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name={`tickets.${index}.quantity`}
              control={control}
              render={({ field }) => (
                <NumberTextFieldNoWheelRHF
                  field={field}
                  fullWidth
                  type="number"
                  label="Quantidade disponível *"
                  InputProps={{ inputProps: { min: 1 } }}
                  error={!!getFieldError(index, 'quantity')}
                  helperText={getFieldError(index, 'quantity')?.message}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name={`tickets.${index}.minPerOrder`}
              control={control}
              defaultValue={1}
              render={({ field }) => (
                <NumberTextFieldNoWheelRHF
                  field={field}
                  fullWidth
                  type="number"
                  label="Mínimo por compra"
                  InputProps={{ inputProps: { min: 1, max: 10 } }}
                  error={!!getFieldError(index, 'minPerOrder')}
                  helperText={
                    getFieldError(index, 'minPerOrder')?.message ||
                    'Ao selecionar, a quantidade começa neste valor'
                  }
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name={`tickets.${index}.maxPerOrder`}
              control={control}
              defaultValue={null}
              render={({ field }) => (
                <NumberTextFieldNoWheelRHF
                  field={{
                    ...field,
                    value: field.value ?? '',
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      const raw = e.target.value;
                      field.onChange(raw === '' ? null : Number(raw));
                    },
                  }}
                  fullWidth
                  type="number"
                  label="Máximo por compra"
                  InputProps={{ inputProps: { min: 1, max: 10 } }}
                  error={!!getFieldError(index, 'maxPerOrder')}
                  helperText={
                    getFieldError(index, 'maxPerOrder')?.message ||
                    'Deixe vazio para usar o limite geral do pedido (10). Valores > 1 ativam “várias pessoas no mesmo pedido”.'
                  }
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name={`tickets.${index}.allowSameAttendeeData`}
              control={control}
              defaultValue={true}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value !== false}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        Permitir os mesmos dados entre participantes
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Ligado (padrão): várias pessoas neste ingresso podem usar o mesmo e-mail. Desligado: cada
                        pessoa precisa de um e-mail diferente.
                      </Typography>
                    </Box>
                  }
                />
              )}
            />
          </Grid>

          {ticketIsPaid && (
            <Grid item xs={12}>
              <Controller
                name={`tickets.${index}.showPlatformFee`}
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Mostrar taxa da plataforma
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Participante vê valor do ingresso e taxa separados
                        </Typography>
                      </Box>
                    }
                  />
                )}
              />
              {watch(`tickets.${index}.showPlatformFee`) && (() => {
                const ticketPrice = parseFloat(watch(`tickets.${index}.price`) || 0) || 0;
                if (ticketPrice <= 0) return null;
                const { feeAmount, total } = calculateTicketTotalsWithFee({
                  unitPrice: ticketPrice,
                  quantity: 1,
                  feePercentage: parishFeePercentage,
                  showPlatformFee: true,
                });
                return (
                  <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: 'info.light', borderColor: 'info.main' }}>
                    <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                      Prévia para o participante
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">Ingresso</Typography>
                      <Typography variant="body2">R$ {ticketPrice.toFixed(2).replace('.', ',')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">Taxa ({parishFeePercentage.toFixed(1).replace('.', ',')}%)</Typography>
                      <Typography variant="body2">R$ {feeAmount.toFixed(2).replace('.', ',')}</Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" fontWeight={600}>Total</Typography>
                      <Typography variant="body2" fontWeight={600} color="primary.main">
                        R$ {total.toFixed(2).replace('.', ',')}
                      </Typography>
                    </Box>
                  </Paper>
                );
              })()}
            </Grid>
          )}

          <Grid item xs={12}>
            <Divider sx={{ my: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'grey.600', fontWeight: 600 }}>
                Período de vendas
              </Typography>
            </Divider>
          </Grid>

          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
              <Controller
                name={`tickets.${index}.startSaleDate`}
                control={control}
                render={({ field }) => (
                  <DateTimePicker
                    label="Início das vendas"
                    value={field.value ?? null}
                    onChange={(date) => field.onChange(date)}
                    ampm={false}
                    format="dd/MM/yyyy HH:mm"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        helperText: 'Data e hora. Use períodos sequenciais para simular lotes (Lote 1 → Lote 2)',
                      },
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
              <Controller
                name={`tickets.${index}.endSaleDate`}
                control={control}
                render={({ field }) => (
                  <DateTimePicker
                    label="Fim das vendas"
                    value={field.value ?? null}
                    onChange={(date) => field.onChange(date)}
                    ampm={false}
                    format="dd/MM/yyyy HH:mm"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        helperText: 'Data e hora do encerramento. Depois disso, crie outro ingresso como próximo lote',
                      },
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'grey.600', fontWeight: 600 }}>
                Controle de acesso
              </Typography>
            </Divider>
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name={`tickets.${index}.hiddenFromPublicPage`}
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch checked={field.value || false} onChange={(e) => field.onChange(e.target.checked)} />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        Ocultar na página do evento
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Só acessível por link direto (gerado abaixo)
                      </Typography>
                    </Box>
                  }
                />
              )}
            />
          </Grid>

          {ticket?.hiddenFromPublicPage && (
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'grey.50',
                }}
              >
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Link para compartilhar
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  Este ingresso não aparece na página pública. Envie o link abaixo para quem pode se inscrever.
                </Typography>
                <InlineCopyLink
                  link={buildHiddenTicketUrl(slug, ticket, brand.key)}
                  fullWidth
                  maxWidth="none"
                />
              </Box>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <Controller
              name={`tickets.${index}.isRestricted`}
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value || false}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                        onToggleRestricted(index, e.target.checked);
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        Lista de convidados restrita
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Apenas e-mails cadastrados podem comprar
                      </Typography>
                    </Box>
                  }
                />
              )}
            />
          </Grid>

          {watch(`tickets.${index}.isRestricted`) && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon fontSize="small" color="primary" />
                  E-mails permitidos
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="email@exemplo.com (Enter para adicionar)"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const input = e.target as HTMLInputElement;
                        onAddEmail(index, input.value);
                        input.value = '';
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonAddIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            size="small"
                            variant="contained"
                            onClick={(e) => {
                              const input = e.currentTarget.parentElement?.parentElement?.querySelector('input') as HTMLInputElement;
                              if (input) {
                                onAddEmail(index, input.value);
                                input.value = '';
                              }
                            }}
                            sx={{ minWidth: 'auto', px: 1 }}
                          >
                            Adicionar
                          </Button>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ flex: 1, minWidth: 200 }}
                  />
                  <input
                    accept=".csv,.txt"
                    style={{ display: 'none' }}
                    id={`import-emails-${index}`}
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        onImportEmails(index, file);
                        e.target.value = '';
                      }
                    }}
                  />
                  <label htmlFor={`import-emails-${index}`}>
                    <IconButton component="span" color="primary" aria-label="Importar CSV">
                      <UploadFileIcon />
                    </IconButton>
                  </label>
                </Box>

                {(() => {
                  const allowedEmails = watch(`tickets.${index}.allowedEmails`) || [];
                  return allowedEmails.length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {allowedEmails.map((email: string) => (
                        <Chip
                          key={email}
                          label={email}
                          onDelete={() => onRemoveEmail(index, email)}
                          deleteIcon={<CloseIcon />}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  ) : (
                    <Alert severity="info">Adicione pelo menos um e-mail para restringir o acesso.</Alert>
                  );
                })()}
              </Paper>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default TicketCardEditor;
